/**
 * Known leveraged and inverse exchange-traded products.
 *
 * These need different treatment from ordinary equity in two places:
 *
 *  1. **Beta is structural, not estimated.** TQQQ's ~3.0 beta to SPY is written
 *     into the prospectus. Clamping it to the [−1, 3] band and shrinking it 33%
 *     toward 1.0 would be correcting for estimation noise that isn't there, and
 *     would understate directional risk by a third. SQQQ is worse: its true beta
 *     near −3 clips to −1 at the floor, turning a large short into a small one.
 *
 *  2. **Market value understates exposure.** $10k of TQQQ is $30k of economic
 *     notional, which is what gross should count.
 *
 * Anything not listed here defaults to 1 and is treated as ordinary equity. The
 * factor is stored per position, so a user can set it for a product missing here.
 */
export const KNOWN_LEVERAGED_ETPS: Record<string, number> = {
  // Broad index, long
  TQQQ: 3, QLD: 2, UPRO: 3, SSO: 2, SPUU: 2, UDOW: 3, DDM: 2, TNA: 3, UWM: 2,
  QQQU: 2, SPXL: 3,
  // Broad index, inverse
  SQQQ: -3, QID: -2, PSQ: -1, SPXU: -3, SDS: -2, SH: -1, SDOW: -3, DOG: -1,
  TZA: -3, TWM: -2, RWM: -1, SPXS: -3,
  // Sector and thematic, long
  SOXL: 3, USD: 2, FNGU: 3, FNGO: 2, TECL: 3, ROM: 2, LABU: 3, CURE: 3,
  FAS: 3, UYG: 2, ERX: 2, DIG: 2, NUGT: 2, JNUG: 2, UGL: 2, AGQ: 2,
  BOIL: 2, UCO: 2, DRN: 3, URE: 2, UTSL: 3, RETL: 3, WANT: 3, DFEN: 3,
  // Sector and thematic, inverse
  SOXS: -3, TECS: -3, LABD: -3, FAZ: -3, SKF: -2, ERY: -2, DUG: -2,
  DUST: -2, JDST: -2, GLL: -2, ZSL: -2, KOLD: -2, SCO: -2, DRV: -3, SRS: -2,
  // Single-stock, long
  NVDL: 2, NVDU: 2, TSLL: 2, TSLT: 2, AAPU: 2, MSFU: 2, AMZU: 2, GGLL: 2,
  METU: 2, AMDL: 2, MUU: 2, PLTU: 2, CONL: 2, MSTU: 2, MSTX: 2, AVL: 2,
  SMCL: 2, COIG: 2, HOOX: 2, BRKU: 2, JPMU: 2, UNIU: 2,
  // Single-stock, inverse
  NVD: -2, TSLQ: -2, TSDD: -2, AAPD: -2, MSFD: -2, AMZD: -2, GGLS: -2,
  METD: -2, AMDD: -2, PLTD: -2, MSTZ: -2, SMCZ: -2, CONI: -2,
  // Volatility
  UVXY: 1.5, UVIX: 2, SVXY: -0.5, SVIX: -1, TVIX: 2,
  // Rates
  TMF: 3, TYD: 3, UBT: 2, TMV: -3, TYO: -3, TBT: -2, TBF: -1, PST: -2,
};

/** Structural leverage for a ticker; 1 when it is ordinary equity. */
export function knownLeverageFactor(ticker: string): number {
  return KNOWN_LEVERAGED_ETPS[ticker.toUpperCase()] ?? 1;
}

/**
 * True when the instrument's beta is a design parameter rather than an estimate.
 *
 * Note this is `!== 1`, not `Math.abs(...) !== 1`: a −1x inverse fund like SH
 * carries 1x notional but a structurally negative beta that sits exactly on the
 * cap floor, so it must skip cap-and-shrink even though its gross is unscaled.
 */
export function isLeveraged(leverageFactor: number): boolean {
  return Number.isFinite(leverageFactor) && leverageFactor !== 1;
}
